import { useMemo } from "react";
import { Database, ExternalLink, Trash } from "lucide-react";
import { Switch } from "@/components/shadcn/switch";
import { Input } from "@/components/shadcn/input";
import { Skeleton } from "@/components/shadcn/skeleton";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { RichTextValue } from "@/components/Form/RichTextField";
import { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";
import { useSpotUser } from "@/utils/spotUser";
import { linkedAccountsUrl } from "@/utils/activeGroup";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useEditorReadOnly } from "@/hooks/useEditorReadOnly";
import type { IntegrationsInfoResponse } from "@/generated";
import { AddNewEntry } from "./AssistantDetailsContent";
import { ExpandableTextarea } from "./ExpandableTextarea";
import { NoxusMenu } from "../UI/NoxusMenu";
import { cn } from "@/lib/utils";
import { UnavailableResourceBadge } from "./UnavailableResourceBadge";

const CONTEXT_FIELD_CONFIG: NoxusFormEntryConfig = {
  type: "str",
  display: {
    type: "rich_text_variables_ai",
    label: "Database context",
    connector_name: "",
    enable_heuristics: true,
    heuristic_context:
      "Context for an attached database: what it holds and when the agent should query it.",
    heuristic_types: ["clarity", "specificity", "actionability"],
  },
};

export type AgentDatabaseEntry = {
  credential_id: string;
  context?: RichTextValue | null;
  enabled?: boolean;
  selected_tables?: string[] | null;
  max_rows?: number | null;
  query_timeout_seconds?: number | null;
};

interface DatabaseCredentialRow {
  id: string;
  display_name: string;
  status: string | null;
  integration_image: string;
}

function collectDatabaseCredentials(
  data: IntegrationsInfoResponse | undefined
): DatabaseCredentialRow[] {
  if (!data) return [];
  const rows: DatabaseCredentialRow[] = [];
  for (const integration of data.integrations) {
    if (integration.type !== "database") continue;
    for (const provider of integration.providers) {
      for (const cred of provider.credentials ?? []) {
        rows.push({
          id: cred.id,
          display_name: cred.display_name ?? integration.display_name,
          status: cred.status ?? null,
          integration_image: integration.image,
        });
      }
    }
  }
  return rows;
}

function useDatabaseCredentials() {
  const { spotGroup } = useSpotUser();
  const isReadOnly = useEditorReadOnly();
  const result = useRemoteData(
    DATA_KEYS.INTEGRATIONS_CONFIG,
    { path: { group_id: spotGroup.id } },
    { enabled: !isReadOnly }
  );
  const data = result.data as IntegrationsInfoResponse | undefined;
  const credentials = useMemo(() => collectDatabaseCredentials(data), [data]);
  return {
    credentials,
    isLoading: result.isLoading,
    catalogLoaded: !isReadOnly && result.isSuccess,
  };
}

const DatabaseRow = ({
  entry,
  credential,
  isMissing,
  isDisabled,
  onChange,
  onRemove,
}: {
  entry: AgentDatabaseEntry;
  credential: DatabaseCredentialRow | undefined;
  isMissing?: boolean;
  isDisabled?: boolean;
  onChange: (next: AgentDatabaseEntry) => void;
  onRemove: () => void;
}) => {
  const enabled = entry.enabled ?? true;
  const name =
    credential?.display_name ??
    (isMissing ? "Disconnected database" : "Database");
  return (
    <div className="flex flex-col border border-border rounded-lg w-full overflow-hidden">
      <div className="flex items-center justify-between gap-2 p-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {credential?.integration_image ? (
            <img
              src={credential.integration_image}
              alt={name}
              className="size-8 rounded-md shrink-0"
            />
          ) : (
            <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-accent text-muted-foreground">
              <Database className="size-4" />
            </div>
          )}
          <p
            className={cn(
              "text-sm font-medium truncate min-w-0",
              isMissing ? "text-muted-foreground" : "flex-1"
            )}
          >
            {name}
          </p>
          {isMissing && <UnavailableResourceBadge kind="database" />}
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={enabled}
            disabled={isDisabled || isMissing}
            onCheckedChange={(checked) =>
              onChange({ ...entry, enabled: checked })
            }
          />
          <NoxusMenu
            isSmall
            isDisabled={isDisabled}
            itemsArray={[
              {
                label: "Remove",
                icon: Trash,
                onClick: onRemove,
                color: "destructive",
              },
            ]}
          />
        </div>
      </div>
      {enabled && !isMissing && (
        <>
          <ExpandableTextarea
            value={entry.context ?? ""}
            onChange={(value) => onChange({ ...entry, context: value })}
            isDisabled={isDisabled}
            label="Database context"
            placeholder="What does this database hold? When should the agent query it?"
            fieldConfig={CONTEXT_FIELD_CONFIG}
          />
          <div className="flex gap-3 px-2 pb-2">
            <label className="flex flex-1 flex-col gap-1 text-xs font-medium text-muted-foreground">
              Max rows
              <Input
                type="number"
                min={1}
                max={10000}
                placeholder="100"
                disabled={isDisabled}
                value={entry.max_rows ?? ""}
                onChange={(e) =>
                  onChange({
                    ...entry,
                    max_rows:
                      e.target.value === ""
                        ? undefined
                        : Number(e.target.value),
                  })
                }
              />
            </label>
            <label className="flex flex-1 flex-col gap-1 text-xs font-medium text-muted-foreground">
              Query timeout (s)
              <Input
                type="number"
                min={1}
                max={300}
                placeholder="30"
                disabled={isDisabled}
                value={entry.query_timeout_seconds ?? ""}
                onChange={(e) =>
                  onChange({
                    ...entry,
                    query_timeout_seconds:
                      e.target.value === ""
                        ? undefined
                        : Number(e.target.value),
                  })
                }
              />
            </label>
          </div>
        </>
      )}
    </div>
  );
};

export const DatabasesField = ({
  databases,
  onDatabasesChange,
  isDisabled,
}: {
  databases: AgentDatabaseEntry[] | undefined;
  onDatabasesChange: (databases: AgentDatabaseEntry[]) => void;
  isDisabled?: boolean;
}) => {
  const { spotGroup } = useSpotUser();
  const { credentials, isLoading, catalogLoaded } = useDatabaseCredentials();
  const byId = useMemo(() => {
    const m = new Map<string, DatabaseCredentialRow>();
    for (const c of credentials) m.set(c.id, c);
    return m;
  }, [credentials]);

  const list = databases ?? [];
  const usedIds = new Set(list.map((e) => e.credential_id));
  const available = credentials.filter((c) => !usedIds.has(c.id));

  const update = (next: AgentDatabaseEntry[]) => {
    if (!isDisabled) onDatabasesChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      <div>
        <p className="text-sm font-medium text-foreground leading-none">
          Databases
        </p>
        <p className="text-xs font-regular text-muted-foreground mt-1.5">
          SQL databases the agent can query read-only, with context. Access is
          deferred, so the agent connects only when a query is needed.
        </p>
      </div>

      {isLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : list.length === 0 && credentials.length === 0 ? (
        <NoxusAlert
          icon={Database}
          description="No databases connected."
          action={{
            label: (
              <>
                <ExternalLink />
                Open connections
              </>
            ),
            onClick: () =>
              window.open(
                linkedAccountsUrl(spotGroup.id),
                "_blank",
                "noopener,noreferrer"
              ),
          }}
        />
      ) : (
        <>
          {list.map((entry, idx) => {
            const key = entry.credential_id || idx;
            const credential = byId.get(entry.credential_id);
            return (
              <DatabaseRow
                key={key}
                entry={entry}
                credential={credential}
                isMissing={!credential && catalogLoaded}
                isDisabled={isDisabled}
                onChange={(next) =>
                  update(list.map((e, i) => (i === idx ? next : e)))
                }
                onRemove={() => update(list.filter((_, i) => i !== idx))}
              />
            );
          })}

          <NoxusMenu
            itemsArray={available.map((c) => ({
              label: c.display_name,
              icon: Database,
              onClick: () =>
                update([
                  ...list,
                  { credential_id: c.id, context: null, enabled: true },
                ]),
            }))}
          >
            <AddNewEntry
              label="Add database"
              disabledLabel={
                available.length === 0 ? "No more databases" : undefined
              }
              onClick={() => {}}
              isDisabled={isDisabled || available.length === 0}
            />
          </NoxusMenu>
        </>
      )}
    </div>
  );
};
